import * as Cesium from 'cesium'
import aircraft from '/@/assets/images/aircraft.gltf'

interface FrustumOptions {
  position: Cesium.Cartesian3;
  heading?: number;
  pitch?: number;
  roll?: number;
  fov?: number;
  near?: number;
  far?: number;
  aspectRatio?: number;
  color?: [number, number, number];
  glbModelShow?: boolean;
  frustumLineShow?: boolean;
  outlineShow?: boolean;
  frustumShow?: boolean;
  id?: string;
  viewer: Cesium.Viewer;
}

class FrustumVisualizer {
  private _heading: number;
  private _pitch: number;
  private _roll: number;
  private _modelHeading: number;
  private _modelPitch: number;
  private _position: Cesium.Cartesian3;
  private _orientation?: Cesium.Quaternion;
  private _fov: number;
  private _near: number;
  private _far: number;
  private _modelHeading: number; // 用户传入的原始偏航（0° ~ 360°）
private _heading: number; // 内部使用的偏航（原始 + 90° 补偿）
  private _aspectRatio?: number;
  private _color: [number, number, number];
  private _glbModelShow?: boolean;
  private _frustumShow?: boolean;
  private _outlineShow?: boolean;
  private _frustumLineShow?: boolean;

  public _frustumPrimitive?: Cesium.Primitive;
  public _priLine?: Cesium.Primitive;
  public _glbModel?: Cesium.Model;
  public _outlinePrimitive?: Cesium.Primitive;
  private _frustum?: Cesium.PerspectiveFrustum;

  public readonly viewer: Cesium.Viewer;

  constructor (options: FrustumOptions) {
    this.viewer = options.viewer
    this._position = options.position
    this._heading = (options.heading || 0) + 90
    this._pitch = options.pitch || 90
    this._roll = options.roll || 0
    this._modelHeading = options.heading || 0
    this._modelPitch = options.pitch || 0
    this._fov = options.fov || 30
    this._near = options.near || 10
    this._far = options.far || 200
    this._color = options.color || [0, 0, 0]
    this._glbModelShow = options.glbModelShow
    this._aspectRatio = options.aspectRatio
    this._frustumShow = options.frustumShow
    this._outlineShow = options.outlineShow
    this._frustumLineShow = options.frustumLineShow
    this.add()
    this.addGlbModel()
  }

  public update (position: Cesium.Cartesian3, heading: number, pitch: number): void {
    this._position = position
    this._pitch = pitch
    this._heading = heading + 90
    this.add()
    this.updatedGlbModelPos()
    this._modelPitch = pitch
    this._modelHeading = heading
  }

  public updateFrustum (position: Cesium.Cartesian3, heading: number, pitch: number): void {
    this._position = position
    this._pitch = pitch
    this._heading = heading + 90
    this.add()
  }

  public updateAircraft (position: Cesium.Cartesian3, heading: number, pitch: number): void {
    this._position = position
    const adjustedHeading = heading + 90
    this.add()
    this.updateGlbModel(adjustedHeading, pitch)
    this._modelPitch = pitch
    this._modelHeading = heading
  }

  public updateFov (fov: number): void {
    this._fov = fov
    this.add()
  }

  public add (): void {
    this.clear()

    const headingPitchRoll = new Cesium.HeadingPitchRoll(
      Cesium.Math.toRadians(this._heading),
      Cesium.Math.toRadians(this._pitch),
      Cesium.Math.toRadians(this._roll)
    )

    this._orientation = Cesium.Transforms.headingPitchRollQuaternion(
      this._position,
      headingPitchRoll
    )

    this.addFrustum()
    this.addOutline()
    this.addFrustumLine()
  }

  public clear (): void {
    this.clearFrustum()
    this.clearOutline()
    this.clearFrustumLine()
  }

  /**
   * 清除所有可视化元素
   * 包括视锥体、轮廓线、视锥体线以及3D模型等所有渲染元素
   */
  public clearAll (): void {
    this.clear()
    this.clearGlbModel()
  }

  private clearFrustum (): void {
    if (this._frustumPrimitive) {
      this.viewer.scene.primitives.remove(this._frustumPrimitive)
      this._frustumPrimitive = undefined
    }
  }

  private clearOutline (): void {
    if (this._outlinePrimitive) {
      this.viewer.scene.primitives.remove(this._outlinePrimitive)
      this._outlinePrimitive = undefined
    }
  }

  private clearFrustumLine (): void {
    if (this._priLine) {
      this.viewer.scene.primitives.remove(this._priLine)
      this._priLine = undefined
    }
  }

  private clearGlbModel (): void {
    if (this._glbModel) {
      this.viewer.scene.primitives.remove(this._glbModel)
      this._glbModel = undefined
    }
  }

  private updatedGlbModelPos (): void {
    if (!this._glbModel) return

    const headingPitchRoll = new Cesium.HeadingPitchRoll(
      Cesium.Math.toRadians(this._heading),
      Cesium.Math.toRadians(this._pitch - 90),
      Cesium.Math.toRadians(this._roll)
    )

    const modelMatrix = Cesium.Transforms.headingPitchRollToFixedFrame(
      this._position,
      headingPitchRoll
    )

    this._glbModel.modelMatrix = modelMatrix
  }

  private updateGlbModel (heading: number, pitch: number): void {
    if (!this._glbModel) return

    const headingPitchRoll = new Cesium.HeadingPitchRoll(
      Cesium.Math.toRadians(heading),
      Cesium.Math.toRadians(pitch - 90),
      Cesium.Math.toRadians(this._roll)
    )

    const modelMatrix = Cesium.Transforms.headingPitchRollToFixedFrame(
      this._position,
      headingPitchRoll
    )

    this._glbModel.modelMatrix = modelMatrix
  }

  private addFrustum (): void {
    if (!this._frustumShow) return
    const frustum = new Cesium.PerspectiveFrustum({
      fov: Cesium.Math.toRadians(this._fov),
      aspectRatio: this._aspectRatio,
      near: this._near,
      far: this._far
    })

    this._frustum = frustum

    const frustumGeometry = new Cesium.FrustumGeometry({
      frustum: frustum,
      origin: this._position,
      orientation: this._orientation,
      vertexFormat: Cesium.VertexFormat.POSITION_ONLY
    })

    const geometryInstance = new Cesium.GeometryInstance({
      geometry: frustumGeometry,
      attributes: {
        color: Cesium.ColorGeometryInstanceAttribute.fromColor(
          new Cesium.Color(
            this._color[0] / 255,
            this._color[1] / 255,
            this._color[2] / 255,
            0.3
          )
        )
      }
    })

    const primitive = new Cesium.Primitive({
      geometryInstances: geometryInstance,
      appearance: new Cesium.PerInstanceColorAppearance({
        closed: true,
        flat: true
      }),
      asynchronous: false
    })

    this._frustumPrimitive = this.viewer.scene.primitives.add(primitive)
  }

  private addFrustumLine (): void {
    if (!this._frustumLineShow) return
    const endPosition = this.getPosByHeadingPitch(this._heading, this._pitch, this._far)

    const polylineGeometry = new Cesium.PolylineGeometry({
      positions: [this._position, endPosition],
      width: 7,
      vertexFormat: Cesium.PolylineMaterialAppearance.VERTEX_FORMAT
    })

    const geometryInstance = new Cesium.GeometryInstance({
      geometry: polylineGeometry
    })

    const material = new Cesium.Material({
      fabric: {
        type: 'PolylineArrow',
        uniforms: {
          color: new Cesium.Color(
            this._color[0] / 255,
            this._color[1] / 255,
            this._color[2] / 255,
            1
          )
        }
      }
    })

    const appearance = new Cesium.PolylineMaterialAppearance({
      material: material,
      renderState: {
        depthTest: { enabled: true }
      }
    })

    const primitive = new Cesium.Primitive({
      geometryInstances: geometryInstance,
      appearance: appearance,
      asynchronous: false
    })

    this._priLine = this.viewer.scene.primitives.add(primitive)
  }

  private addOutline (): void {
    if (!this._outlineShow) return
    const frustum = new Cesium.PerspectiveFrustum({
      fov: Cesium.Math.toRadians(this._fov),
      aspectRatio: this._aspectRatio,
      near: this._near,
      far: this._far
    })

    const frustumOutlineGeometry = new Cesium.FrustumOutlineGeometry({
      frustum: frustum,
      origin: this._position,
      orientation: this._orientation
    })

    const geometryInstance = new Cesium.GeometryInstance({
      geometry: frustumOutlineGeometry,
      attributes: {
        color: Cesium.ColorGeometryInstanceAttribute.fromColor(
          new Cesium.Color(
            this._color[0] / 255,
            this._color[1] / 255,
            this._color[2] / 255,
            1
          )
        )
      }
    })

    const primitive = new Cesium.Primitive({
      geometryInstances: geometryInstance,
      appearance: new Cesium.PerInstanceColorAppearance({
        closed: true,
        flat: true
      }),
      asynchronous: false
    })

    this._outlinePrimitive = this.viewer.scene.primitives.add(primitive)
  }

  private async addGlbModel (): Promise<void> {
    const headingPitchRoll = new Cesium.HeadingPitchRoll(
      Cesium.Math.toRadians(this._heading),
      Cesium.Math.toRadians(this._pitch - 90),
      Cesium.Math.toRadians(this._roll)
    )

    const modelMatrix = Cesium.Transforms.headingPitchRollToFixedFrame(
      this._position,
      headingPitchRoll
    )

    const model = await Cesium.Model.fromGltfAsync({
      url: aircraft,
      show: this._glbModelShow,
      modelMatrix: modelMatrix,
      scale: 0.1,
      minimumPixelSize: 0.1,
      maximumScale: 0.6,
      asynchronous: false
    })

    this._glbModel = this.viewer.scene.primitives.add(model)
  }

  public getPosByHeading (heading: number, distance: number = 2): Cesium.Cartesian3 {
    const x = -Math.cos(2 * Math.PI / 360 * heading)
    const y = Math.sin(2 * Math.PI / 360 * heading)

    const enuMatrix = Cesium.Transforms.eastNorthUpToFixedFrame(this._position)
    const point = Cesium.Matrix4.multiplyByPoint(
      enuMatrix,
      new Cesium.Cartesian3(x, y, 0),
      new Cesium.Cartesian3()
    )

    const normal = Cesium.Cartesian3.normalize(this._position, new Cesium.Cartesian3())
    let direction = Cesium.Cartesian3.subtract(this._position, point, new Cesium.Cartesian3())
    direction = Cesium.Cartesian3.normalize(direction, direction)

    const cross = Cesium.Cartesian3.cross(direction, normal, new Cesium.Cartesian3())
    const normalizedCross = Cesium.Cartesian3.normalize(cross, cross)

    return new Cesium.Cartesian3(
      this._position.x + normalizedCross.x * distance,
      this._position.y + normalizedCross.y * distance,
      this._position.z + normalizedCross.z * distance
    )
  }

  private getPosByHeadingPitch (heading: number, pitch: number, distance: number): Cesium.Cartesian3 {
    const enuMatrix = Cesium.Transforms.eastNorthUpToFixedFrame(this._position)

    const x = 0
    const y = distance * Math.sin(pitch / 180 * Math.PI)
    const z = distance * Math.cos(pitch / 180 * Math.PI)

    const transformedX = x * Math.sin(heading / 180 * Math.PI) - y * Math.cos(heading / 180 * Math.PI)
    const transformedY = y * Math.sin(heading / 180 * Math.PI) + x * Math.cos(heading / 180 * Math.PI)
    const transformedZ = z

    return Cesium.Matrix4.multiplyByPoint(
      enuMatrix,
      new Cesium.Cartesian3(transformedX, transformedY, transformedZ),
      new Cesium.Cartesian3()
    )
  }

  public show (): void {
    if (this._glbModel) {
      this._glbModel.show = this._glbModelShow || false
    }
    if (this._outlinePrimitive) this._outlinePrimitive.show = true
    if (this._priLine) this._priLine.show = true
    if (this._frustumPrimitive) this._frustumPrimitive.show = true
  }

  public hide (): void {
    if (this._glbModel) this._glbModel.show = false
    if (this._outlinePrimitive) this._outlinePrimitive.show = false
    if (this._priLine) this._priLine.show = false
    if (this._frustumPrimitive) this._frustumPrimitive.show = false
  }

  // Getters and setters for key properties
  public get position (): Cesium.Cartesian3 {
    return this._position
  }

  public set position (value: Cesium.Cartesian3) {
    this._position = value
    this.add()
  }

  public get yaw ():number {
    return this._heading
  }

  public set yaw (value:number) {
    this._heading = value
    this.add()
  }

  public get gimbalYaw ():number {
    return this._modelHeading
  }

  public set gimbalYaw (value:number) {
    this._modelHeading = value
    this.add()
  }

  public get gimbalPitch ():number {
    return this._modelPitch
  }

  public set gimbalPitch (value:number) {
    this._modelPitch = value
    this.add()
  }

  public get heading (): number {
    return this._modelHeading
  }

  public set heading (value: number) {
    this._modelHeading = value
    this._heading = value + 90
    this.add()
  }

  public get pitch (): number {
    return this._pitch
  }

  public set pitch (value: number) {
    this._modelPitch = value
    this._pitch = value
    this.add()
  }

  public get roll (): number {
    return this._roll
  }

  public set roll (value: number) {
    this._roll = value
    this.add()
  }

  public get fov (): number {
    return this._fov
  }

  public set fov (value: number) {
    this._fov = value
    this.add()
  }

  public get color (): [number, number, number] {
    return this._color
  }

  public set color (value: [number, number, number]) {
    this._color = value
    this.add()
  }
}

export { FrustumVisualizer as FrustumTool, FrustumOptions }
